import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from "recharts";
import { formatMoney } from "@/lib/currency";

const CHART_COLORS = [
  "#2D5A40", "#86A893", "#E09E50", "#C45B4D", "#D0D9D3", "#437A55", "#A16A25", "#576A5E",
];

// Recharts prop objects that never change — hoisted to module scope so
// they're referentially stable across renders (avoids re-render churn
// inside ResponsiveContainer children).
const CASHFLOW_MARGIN = { top: 10, right: 8, left: 0, bottom: 0 };
const BREAKDOWN_MARGIN = { top: 0, right: 8, left: 0, bottom: 0 };
const INITIAL_DIMENSION = { width: 1, height: 1 };
const BAR_RADIUS = [0, 6, 6, 0];

/**
 * ChartsRow — 6-month cash-flow area chart + this-month expense bar chart.
 * Extracted from Dashboard.jsx in iter-31.
 */
export default function ChartsRow({ cashflow, breakdown, currency }) {
  const cur = currency;

  return (
    <div className="grid lg:grid-cols-3 gap-5 mb-6">
      {/* Cash flow */}
      <div className="lg:col-span-2 card-flat p-6 animate-fadeup">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow">Cash Flow</div>
            <div className="text-base font-medium mt-1">6-month view</div>
          </div>
          <div className="flex gap-3 text-xs text-[var(--text-muted)]">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#2D5A40]"/> Income</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#C45B4D]"/> Expense</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#E09E50]"/> Net</span>
          </div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_DIMENSION}>
            <AreaChart data={cashflow} margin={CASHFLOW_MARGIN}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2D5A40" stopOpacity={0.35}/>
                  <stop offset="100%" stopColor="#2D5A40" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C45B4D" stopOpacity={0.28}/>
                  <stop offset="100%" stopColor="#C45B4D" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E2E4E0" strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="month" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `${v>=1000?`${(v/1000).toFixed(0)}k`:v}`}/>
              <Tooltip formatter={(v) => formatMoney(v, cur)} />
              <Area type="monotone" dataKey="income" stroke="#2D5A40" strokeWidth={2} fill="url(#gInc)" />
              <Area type="monotone" dataKey="expense" stroke="#C45B4D" strokeWidth={2} fill="url(#gExp)" />
              <Area type="monotone" dataKey="net" stroke="#E09E50" strokeWidth={2} fillOpacity={0} strokeDasharray="4 3" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Expense breakdown */}
      <div className="card-flat p-6 animate-fadeup">
        <div className="label-eyebrow">Expense Breakdown</div>
        <div className="text-base font-medium mt-1">This month</div>
        {breakdown.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] mt-8">No expenses yet.</div>
        ) : (
          <>
            <div style={{ width: "100%", height: 200 }} className="mt-4">
              <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_DIMENSION}>
                <BarChart data={breakdown.slice(0, 6)} layout="vertical" margin={BREAKDOWN_MARGIN}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="category" width={90} tickLine={false} axisLine={false}/>
                  <Tooltip formatter={(v) => formatMoney(v, cur)} />
                  <Bar dataKey="amount" radius={BAR_RADIUS}>
                    {breakdown.slice(0, 6).map((b, i) => (
                      <Cell key={b.category || `bar-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]}/>
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-xs text-[var(--text-muted)]">
              Top: <span className="text-[var(--text-primary)] font-medium">{breakdown[0]?.category}</span> ·{" "}
              {breakdown[0]?.percent}% of spending
            </div>
          </>
        )}
      </div>
    </div>
  );
}
